<script setup>
// Segmented tab switcher used at the top of list pages. The parent owns the
// selected id (modelValue) and reacts to @change with the whole tab object,
// which is what the existing changeTab(tab) handlers expect. A tab with a
// `count` above zero shows it as a pill, e.g. items waiting for action.
defineProps({
  items: { type: Array, required: true }, // [{ id, name, count? }]
  modelValue: { type: [Number, String], default: null },
});

const emit = defineEmits(['update:modelValue', 'change']);

function select(item) {
  emit('update:modelValue', item.id);
  emit('change', item);
}
</script>

<template>
  <div class="page-tabs" role="tablist">
    <button
      v-for="item in items"
      :key="item.id"
      type="button"
      role="tab"
      class="page-tabs__tab"
      :class="{ 'page-tabs__tab--active': item.id === modelValue }"
      :aria-selected="item.id === modelValue"
      @click="select(item)"
    >
      {{ item.name }}
      <span v-if="item.count > 0" class="page-tabs__count">{{ item.count > 99 ? '99+' : item.count }}</span>
    </button>
  </div>
</template>

<style scoped>
.page-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  background-color: var(--accent);
  border-radius: 10px;
}

.page-tabs__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--secondary-foreground);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.page-tabs__tab:hover {
  color: var(--foreground);
}

.page-tabs__tab--active {
  background-color: var(--background);
  color: var(--foreground);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.page-tabs__count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background-color: var(--brand);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.page-tabs__tab:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 1px;
}
</style>
